// Benchmarking Workflow State Management
// Manages the 4-step cell selection workflow

import { signal, computed, effect } from '@preact/signals'
import { safeLocal } from '../utils/safeStorage.js'
import { fullCellsCache } from './globalSignals.js'

/**
 * Workflow Steps:
 * 1. fundamentals  - Set basic filters (Format, Technology, Chemistry)
 * 2. performance   - Define performance requirements (My Plot with Requirement Zone)
 * 3. priorities    - Tune priorities (Score Plot with weights, select Top 5)
 * 4. champion      - Pick champion (Comparison table with ranking)
 */

// Current workflow step (1-4)
export const currentStep = signal(1)

// All available cells (loaded from API - minimal data from explorer endpoint)
export const workflowAllCells = signal([])

/**
 * Select the wizard's candidate pool from the full catalogue.
 *
 * DEMO users keep the locked shadow cells: they carry base KPIs (format,
 * chemistry, energy, power) — only impedance/thermal/quality are gated — and
 * are shown across the app, so dropping them would collapse the wizard to the
 * ~30 unlocked cells. This mirrors MainAppPage's `isDemoCatalogUnlocked()`
 * exception for the explorer plot/grid.
 *
 * Free-tier users still exclude locked cells (their locked cells carry no
 * usable data). Pure + `isDemo` injected so it stays unit-testable.
 *
 * @param {Array} cells - full catalogue (allCells.value)
 * @param {boolean} isDemo - true for DEMO-tier users (isDemoCatalogUnlocked())
 * @returns {Array} candidate pool (new array)
 */
export function selectWorkflowPool(cells, isDemo) {
  if (!Array.isArray(cells)) return []
  return isDemo ? [...cells] : cells.filter(cell => !cell.is_locked)
}

// Fully loaded cells with DCC data (loaded lazily when needed for Step 2)
// Key = cellId, Value = full cell data with DCC arrays
// NOTE: This is now a reference to the global fullCellsCache for sharing with DccLoader
export const workflowFullCellsCache = fullCellsCache

// Loading state for DCC data
export const workflowDccDataLoading = signal(false)
export const workflowDccDataProgress = signal(0) // 0-100

// Step 1: Fundamentals - Filter selections
export const selectedFormats = signal([])
export const selectedTechnologies = signal([])
export const selectedChemistries = signal([])

// Step 1: Filtered cells after fundamentals (minimal data)
export const fundamentalsCells = computed(() => {
  const cells = workflowAllCells.value
  const formats = selectedFormats.value
  const technologies = selectedTechnologies.value
  const chemistries = selectedChemistries.value
  
  if (!cells || cells.length === 0) return []
  
  // If no filters selected, return all cells
  if (formats.length === 0 && technologies.length === 0 && chemistries.length === 0) {
    return cells
  }
  
  return cells.filter(cell => {
    const format = cell.format || cell.mechanical?.format || cell.mechanical?.format_group
    const tech = cell.technology || cell.general?.technology
    const chem = cell.main_chemistry || cell.chemical?.main_chemistry
    
    const formatMatch = formats.length === 0 || formats.includes(format)
    const techMatch = technologies.length === 0 || technologies.includes(tech)
    const chemMatch = chemistries.length === 0 || chemistries.includes(chem)
    
    return formatMatch && techMatch && chemMatch
  })
})

// Step 2: Filtered cells with full DCC data (loaded lazily)
// Uses the cached full cell data for cells that have been loaded
// IMPORTANT: Only returns cells that actually have DCC arrays loaded!
export const fundamentalsCellsWithDcc = computed(() => {
  const minimalCells = fundamentalsCells.value
  const fullCellsCache = workflowFullCellsCache.value
  
  if (!minimalCells || minimalCells.length === 0) return []
  
  // Only return cells that have full DCC data loaded
  // Filter out cells that don't have full data yet (don't return minimalCell as fallback)
  const cellsWithDcc = []
  for (const minimalCell of minimalCells) {
    const cellId = minimalCell.id || minimalCell.product_id
    const fullCell = fullCellsCache.get(cellId)
    if (fullCell) {
      cellsWithDcc.push(fullCell)
    }
    // Don't add minimalCell as fallback - it doesn't have DCC arrays
  }
  return cellsWithDcc
})

// Step 2: Performance - My Plot settings with DCC requirements
export const performanceXAxis = signal('energy_c10_grav')
export const performanceYAxis = signal('power_peak_grav')

// Requirement signals (matching MyPlot pattern)
// These are used for DCC interpolation and quadrant visualization
export const workflowRequiredPower = signal(null) // X-axis requirement (W/kg, W/L, or W)
export const workflowRequiredEnergy = signal(null) // Y-axis requirement (Wh/kg, Wh/L, or Wh)

// Legacy zone structure (keep for compatibility)
export const performanceRequirementZone = signal({
  minX: null,
  maxX: null,
  minY: null,
  maxY: null
})

// Display options (matching MyPlot)
export const workflowShowPareto = signal(false)
export const workflowShowBenchmarks = signal(false)
export const workflowShowLegend = signal(false)
export const workflowShowLabels = signal(false)

// Step 2: Cells meeting requirements - Updated by Step2Performance component
// This signal is updated by Step2 after DCC calculation to avoid circular dependency
export const performanceCellsMeetingRequirements = signal([])

// Step 2: DCC evaluation status - Used to allow navigation even when no DCC data exists
export const workflowDccEvaluationComplete = signal(false)
export const workflowDccEvaluatedCellCount = signal(0)

// Step 2: Cells within requirement zone (DCC-based filtering)
// Returns cells that meet BOTH requirements after DCC interpolation
export const performanceCells = computed(() => {
  const cells = fundamentalsCells.value
  
  if (!cells || cells.length === 0) return []
  
  // If no requirements set, return all cells from Step 1
  if (workflowRequiredPower.value === null || workflowRequiredEnergy.value === null) {
    return cells
  }
  
  // Return cells meeting requirements (updated by Step2Performance component)
  // This avoids circular dependency with calculateRequirements
  return performanceCellsMeetingRequirements.value.length > 0 
    ? performanceCellsMeetingRequirements.value 
    : cells
})

// Step 3: Priorities - Score weights
export const scoreWeights = signal({
  energy: 1.0,
  power: 1.0,
  efficiency: 1.0,
  aging: 1.0,
  batch: 1.0,
  mass: 1.0
})

// Step 3: Cells with calculated scores, sorted descending
export const priorityCellsWithScores = computed(() => {
  const cells = performanceCells.value
  const weights = scoreWeights.value
  
  if (!cells || cells.length === 0) return []
  
  // Calculate score for each cell
  const cellsWithScores = cells.map(cell => ({
    ...cell,
    benchmarkScore: calculateBenchmarkScore(cell, weights)
  }))
  
  // Sort by score descending
  return cellsWithScores.sort((a, b) => b.benchmarkScore - a.benchmarkScore)
})

// Step 3: Top 5 cells (or fewer if less available)
export const priorityCells = computed(() => {
  const cells = priorityCellsWithScores.value
  return cells.slice(0, Math.min(5, cells.length))
})

// Step 4: Champion cells (same as priorityCells, but for final comparison)
export const championCells = computed(() => priorityCells.value)

// Helper: Get axis value from cell data
function getAxisValue(cell, axisId) {
  // Map axis IDs to cell data paths
  const axisMap = {
    'energy_c10_abs': cell.electrical?.E_c10_Wh,
    'energy_c10_grav': cell.electrical?.E_grav_Wh_per_kg,
    'energy_c10_vol': cell.electrical?.E_vol_Wh_per_L,
    'power_peak_abs': cell.electrical?.P_peak_W,
    'power_peak_grav': cell.electrical?.P_grav_W_per_kg,
    'power_peak_vol': cell.electrical?.P_vol_W_per_L,
    'capacity_c10_abs': cell.electrical?.C_c10_Ah,
    'capacity_c10_grav': cell.electrical?.C_grav_Ah_per_kg,
    'capacity_c10_vol': cell.electrical?.C_vol_Ah_per_L,
    'efficiency': cell.electrical?.eta_dc_pct,
    'mass': cell.mechanical?.mass_g,
  }
  
  return axisMap[axisId]
}

// Helper: Calculate benchmark score (0-100)
function calculateBenchmarkScore(cell, weights) {
  // Normalize each metric to 0-100 scale
  const metrics = {
    energy: Math.min(100, ((cell.electrical?.E_grav_Wh_per_kg || 0) / 300) * 100), // Max ~300 Wh/kg
    power: Math.min(100, ((cell.electrical?.P_grav_W_per_kg || 0) / 5000) * 100), // Max ~5000 W/kg
    efficiency: (cell.electrical?.eta_dc_pct || 0), // Already 0-100
    aging: (1 - Math.abs(cell.aging?.CAP_loss_rel || 0)) * 100, // Lower loss = better (0-100)
    batch: (1 - (cell.batch?.CAP_spread_std_rel || 0)) * 100, // Lower variation = better (0-100)
    mass: Math.min(100, (1 / (cell.mechanical?.mass_g || 100)) * 10000) // Lower mass = better, normalized
  }
  
  // Weighted sum (normalized to 0-100)
  const totalWeight = Object.values(weights).reduce((sum, w) => sum + w, 0)
  if (totalWeight === 0) return 0
  
  const weightedSum = Object.entries(metrics).reduce((sum, [key, value]) => {
    return sum + (value * weights[key])
  }, 0)
  
  // Scale to 0-100
  return Math.min(100, Math.max(0, weightedSum / totalWeight))
}

// Actions
export function goToStep(step) {
  if (step >= 1 && step <= 3) {
    currentStep.value = step
  }
}

export function nextStep() {
  if (currentStep.value < 3) {
    currentStep.value++
  }
}

export function previousStep() {
  if (currentStep.value > 1) {
    currentStep.value--
  }
}

export function resetWorkflow() {
  currentStep.value = 1
  selectedFormats.value = []
  selectedTechnologies.value = []
  selectedChemistries.value = []
  performanceRequirementZone.value = { minX: null, maxX: null, minY: null, maxY: null }
  workflowDccEvaluationComplete.value = false
  workflowDccEvaluatedCellCount.value = 0
  scoreWeights.value = {
    energy: 1.0,
    power: 1.0,
    efficiency: 1.0,
    aging: 1.0,
    batch: 1.0,
    mass: 1.0
  }
}

// Persistence: Save to localStorage on change
effect(() => {
  const state = {
    currentStep: currentStep.value,
    selectedFormats: selectedFormats.value,
    selectedTechnologies: selectedTechnologies.value,
    selectedChemistries: selectedChemistries.value,
    performanceXAxis: performanceXAxis.value,
    performanceYAxis: performanceYAxis.value,
    performanceRequirementZone: performanceRequirementZone.value,
    workflowRequiredPower: workflowRequiredPower.value,
    workflowRequiredEnergy: workflowRequiredEnergy.value,
    workflowShowPareto: workflowShowPareto.value,
    workflowShowBenchmarks: workflowShowBenchmarks.value,
    workflowShowLegend: workflowShowLegend.value,
    workflowShowLabels: workflowShowLabels.value,
    scoreWeights: scoreWeights.value
  }
  
  safeLocal.set('benchmarkingWorkflow', JSON.stringify(state))
})

// Restore from localStorage on load
export function restoreWorkflowState() {
  try {
    const saved = safeLocal.get('benchmarkingWorkflow')
    if (saved) {
      const state = JSON.parse(saved)
      currentStep.value = state.currentStep || 1
      selectedFormats.value = state.selectedFormats || []
      selectedTechnologies.value = state.selectedTechnologies || []
      selectedChemistries.value = state.selectedChemistries || []
      performanceXAxis.value = state.performanceXAxis || 'energy_c10_grav'
      performanceYAxis.value = state.performanceYAxis || 'power_peak_grav'
      performanceRequirementZone.value = state.performanceRequirementZone || { minX: null, maxX: null, minY: null, maxY: null }
      workflowRequiredPower.value = state.workflowRequiredPower ?? null
      workflowRequiredEnergy.value = state.workflowRequiredEnergy ?? null
      workflowShowPareto.value = state.workflowShowPareto ?? false
      workflowShowBenchmarks.value = state.workflowShowBenchmarks ?? false
      workflowShowLegend.value = state.workflowShowLegend ?? false
      workflowShowLabels.value = state.workflowShowLabels ?? false
      scoreWeights.value = state.scoreWeights || {
        energy: 1.0,
        power: 1.0,
        efficiency: 1.0,
        aging: 1.0,
        batch: 1.0,
        mass: 1.0
      }
    }
  } catch (e) {
    console.warn('[BenchmarkingWorkflow] Failed to restore state:', e)
  }
}
